import { Modal } from "@components/Modal";
import { FullPicker } from "@components/ImagePicker/FullPicker";
import { FirebaseFreshImage } from "@components/FirebaseCachedImage/FirebaseFreshImage";
import { DeleteIcon } from "@components/Icons/Delete";
import { FC, useContext, useState } from "react";
import { useTranslation } from "@helpers/useTranslation";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { getMediaUrl } from "@helpers/getMediaUrl";
import { ref, uploadBytes, deleteObject } from "firebase/storage";

type Props = {
  show: boolean;
  onClose: () => void;
  questionId: string;
  challengeId: string;
  questionHasPhoto: boolean;
  onPhotoChange: (questionHasPhoto: boolean) => void;
};

export const QuestionPhotoModal: FC<Props> = ({
  show,
  onClose,
  questionId,
  challengeId,
  questionHasPhoto,
  onPhotoChange,
}) => {
  const { t } = useTranslation("admin");
  const { storage } = useContext(FirebaseContext);
  const eventId = useEventId();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleNewImage = async (file: File) => {
    setUploading(true);
    try {
      // Storage path: events/{eventId}/challenges/{challengeId}/{questionId}
      const storagePath = `events/${eventId}/challenges/${challengeId}/${questionId}`;
      const storageRef = ref(storage, storagePath);

      // Upload with metadata to identify as question photo
      const metadata = {
        customMetadata: {
          isQuestionPhoto: "true",
          challengeId: challengeId,
          questionId: questionId,
          eventId: eventId,
        },
      };

      await uploadBytes(storageRef, file, metadata);

      // Set questionHasPhoto to true
      onPhotoChange(true);
    } catch (error) {
      console.error("Error uploading question photo:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!questionHasPhoto) return;

    setDeleting(true);
    try {
      // Storage path: events/{eventId}/challenges/{challengeId}/{questionId}
      const baseUri = `events/${eventId}/challenges/${challengeId}/${questionId}`;

      const lightRef = ref(storage, `${baseUri}_light`);
      const heavyRef = ref(storage, `${baseUri}_heavy`);
      const thumbRef = ref(storage, `${baseUri}_thumb`);

      // Try to delete all versions (some might not exist, which is fine)
      await Promise.allSettled([
        deleteObject(lightRef),
        deleteObject(heavyRef),
        deleteObject(thumbRef),
      ]);

      onPhotoChange(false);
    } catch (error) {
      console.error("Error deleting question photo:", error);
    } finally {
      setDeleting(false);
    }
  };

  // Construct photo URI for display
  const photoUri = questionHasPhoto ? `events/${eventId}/challenges/${challengeId}/${questionId}_light` : undefined;
  const photoUrl = photoUri ? getMediaUrl(storage, photoUri) : undefined;

  return (
    <Modal className="z-50" show={show} onCancel={onClose} size="lg">
      <h3 className="text-lg font-bold mb-4">{t("questionPhoto")}</h3>

      {questionHasPhoto && photoUri && photoUrl ? (
        <div className="flex flex-col items-center">
          <div className="mb-4 max-w-md">
            <FirebaseFreshImage uri={photoUri} alt={t("questionPhoto")} />
          </div>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={`btn-error btn ${deleting ? "loading" : ""}`}
            >
              <DeleteIcon className="mr-2 h-4 w-4" />
              {t("delete")}
            </button>
            <button type="button" onClick={onClose} className="btn">
              {t("close")}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <FullPicker
            uploadLabel={t("uploadQuestionPhoto")}
            reuploadLabel={t("reuploadQuestionPhoto")}
            onNewImage={handleNewImage}
            allowUpload={true}
          />
          {uploading && (
            <div className="flex justify-center mt-4">
              <div className="loading loading-spinner loading-lg"></div>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onClose} className="btn">
              {t("close")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

